import { useMemo, useEffect, useCallback } from 'react'
import {
  getImage,
  getPersonName,
  getNestedOrFloatingElementById,
  useSlsStateContext,
  useSlsDispatchContext,
  getRelationshipToPrimaryPersonDisplayLabel,
  getRelationshipToPrimaryPersonText,
  getPrimaryRelationshipText,
} from '@fs/zion-sls'
import { useTranslation } from 'react-i18next'
import { useR2StateContext } from '../../RIP/providers/r2Provider/R2Context'
import getPersonFullNameFromStandards from '../services/standards/names'

/**
 * Custom hook for managing the currently selected person in the Record Image Processor (RIP).
 *
 * This hook provides comprehensive information about the selected person, including their name,
 * relationship to the primary person on the record, and handles fetching localized name data
 * from standards when needed.
 *
 * @returns {Object} Selected person information and utilities
 * @returns {string} selectedPersonName - The display name of the selected person
 * @returns {Object} selectedPerson - The complete person object with all properties
 * @returns {Object} currentImage - The current image object being viewed
 * @returns {string} primaryRelationshipText - Combined text describing the relationship to the primary person
 * @returns {string} relationshipText - The relationship description text
 * @returns {Function} refreshLocalizedName - Async function to refresh the person's localized name from standards
 *
 * @example
 * const {
 *   selectedPersonName,
 *   selectedPerson,
 *   currentImage,
 *   primaryRelationshipText,
 *   relationshipText,
 *   refreshLocalizedName
 * } = useSelectedPerson()
 */
export const useSelectedPerson = () => {
  const [t] = useTranslation()
  const zionSlsState = useSlsStateContext()
  const zionSlsDispatch = useSlsDispatchContext()
  const r2State = useR2StateContext()

  const currentImage = useMemo(
    () => getImage(zionSlsState.images, zionSlsState.currentImageArk),
    [zionSlsState.images, zionSlsState.currentImageArk]
  )
  const record = useMemo(
    () => getNestedOrFloatingElementById([currentImage], r2State?.selectedRecordId),
    [currentImage, r2State?.selectedRecordId]
  )
  const selectedPerson = useMemo(
    () => getNestedOrFloatingElementById([currentImage], r2State?.selectedPersonId),
    [currentImage, r2State.selectedPersonId]
  )
  const selectedPersonName = useMemo(
    () => getPersonName({ person: selectedPerson }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedPerson, zionSlsState.images]
  )
  const primaryRelationshipLabel = useMemo(
    () => getRelationshipToPrimaryPersonDisplayLabel(selectedPerson, zionSlsState.types, record),
    [selectedPerson, zionSlsState.types, record]
  )
  const relationshipText = useMemo(
    () => getRelationshipToPrimaryPersonText(selectedPerson, zionSlsState.types, record, t),
    [selectedPerson, zionSlsState.types, record, t]
  )
  const primaryRelationshipText = useMemo(
    () => getPrimaryRelationshipText(primaryRelationshipLabel, relationshipText),
    [primaryRelationshipLabel, relationshipText]
  )

  const refreshLocalizedName = useCallback(async () => {
    if (selectedPerson?.id) {
      const localizedNameInfo = await getPersonFullNameFromStandards(selectedPerson)
      // Verify the person hasn't changed before dispatching
      if (localizedNameInfo.id === r2State?.selectedPersonId) {
        zionSlsDispatch({
          type: 'SET_PERSON_LOCALIZED_NAME',
          value: {
            personId: localizedNameInfo.id,
            localizedName: localizedNameInfo.localizedName,
            partitionKey: currentImage?.partitionKey,
          },
        })
      }
    }
  }, [selectedPerson, zionSlsDispatch, currentImage?.partitionKey, r2State?.selectedPersonId])

  useEffect(() => {
    if (selectedPerson?.id && !selectedPerson?.localizedName) {
      refreshLocalizedName()
    }
  }, [selectedPerson, selectedPerson?.localizedName, refreshLocalizedName])

  return {
    selectedPersonName,
    selectedPerson,
    currentImage,
    primaryRelationshipText,
    relationshipText,
    refreshLocalizedName,
  }
}
